import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../hooks/useAuth";
import { adminGet } from "../services/api";
import { GlobeRegular, EyeRegular, CursorClickRegular, SparkleRegular } from "@fluentui/react-icons";
import { fluentIcon } from "../lib/fluentIcon";

const Globe = fluentIcon(GlobeRegular);
const Eye = fluentIcon(EyeRegular);
const MousePointerClick = fluentIcon(CursorClickRegular);
const Sparkles = fluentIcon(SparkleRegular);

interface WebAnalytics {
  totals: { pageviews_24h: number; pageviews_7d: number; events_24h: number };
  pageviews_by_day: { day: string; views: number }[];
  top_paths: { path: string; views: number }[];
  events_by_name: { name: string; total_7d: number; total_24h: number }[];
  key_events_by_day: { day: string; name: string; count: number }[];
}

function StatCard({ icon: Icon, label, value }: { icon: typeof Eye; label: string; value: number | string }) {
  return (
    <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-5">
      <div className="flex items-center gap-2 text-slate-400 text-xs uppercase tracking-wider mb-2">
        <Icon size={14} />
        {label}
      </div>
      <div className="text-2xl font-bold text-white">{value}</div>
    </div>
  );
}

function Bars({ rows }: { rows: { day: string; views: number }[] }) {
  const max = Math.max(1, ...rows.map((r) => r.views));
  return (
    <div className="flex items-end gap-1.5 h-28">
      {rows.map((r) => (
        <div key={String(r.day)} className="flex-1 flex flex-col items-center gap-1" title={`${r.day}: ${r.views}`}>
          <div
            className="w-full rounded-t bg-blue-500/70 hover:bg-blue-400 transition-colors"
            style={{ height: `${Math.max(3, (r.views / max) * 100)}%` }}
          />
          <span className="text-[9px] text-slate-500">{String(r.day).slice(8, 10)}</span>
        </div>
      ))}
    </div>
  );
}

export default function WebAnalyticsPage() {
  const { token } = useAuth();
  const { data, isLoading, error } = useQuery<WebAnalytics>({
    queryKey: ["web-analytics"],
    queryFn: () => adminGet<WebAnalytics>("/api/v1/admin/web-analytics", token),
    refetchInterval: 60_000,
  });

  const bodh7d = data?.events_by_name.find((e) => e.name === "bodh_view")?.total_7d ?? 0;

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Globe size={22} className="text-blue-400" />
          Web Analytics
        </h1>
        <p className="text-slate-400 text-sm mt-1">
          news.tomitra.com — first-party, cookie-free. Watch whether Bodh is landing.
        </p>
      </div>

      {isLoading && <p className="text-slate-400">Loading…</p>}
      {error && <p className="text-red-400 text-sm">{String(error)}</p>}

      {data && (
        <div className="space-y-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={Eye} label="Pageviews · 24h" value={data.totals.pageviews_24h} />
            <StatCard icon={Eye} label="Pageviews · 7d" value={data.totals.pageviews_7d} />
            <StatCard icon={MousePointerClick} label="Events · 24h" value={data.totals.events_24h} />
            <StatCard icon={Sparkles} label="Bodh views · 7d" value={bodh7d} />
          </div>

          <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-5">
            <h2 className="text-sm font-semibold text-slate-300 mb-4">Pageviews — last 14 days</h2>
            <Bars rows={data.pageviews_by_day} />
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-5">
              <h2 className="text-sm font-semibold text-slate-300 mb-3">Events — last 7 days</h2>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-slate-500 text-xs uppercase">
                    <th className="text-left pb-2">Event</th>
                    <th className="text-right pb-2">24h</th>
                    <th className="text-right pb-2">7d</th>
                  </tr>
                </thead>
                <tbody>
                  {data.events_by_name.map((e) => (
                    <tr key={e.name} className="border-t border-slate-700/60">
                      <td className="py-2 text-slate-200 font-mono text-xs">{e.name}</td>
                      <td className="py-2 text-right text-slate-300">{e.total_24h}</td>
                      <td className="py-2 text-right text-white font-semibold">{e.total_7d}</td>
                    </tr>
                  ))}
                  {data.events_by_name.length === 0 && (
                    <tr>
                      <td colSpan={3} className="py-4 text-slate-500 text-center">
                        No events yet — they appear as readers use the site.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-5">
              <h2 className="text-sm font-semibold text-slate-300 mb-3">Top pages — last 7 days</h2>
              <table className="w-full text-sm">
                <tbody>
                  {data.top_paths.map((p) => (
                    <tr key={p.path} className="border-t border-slate-700/60 first:border-t-0">
                      <td className="py-2 text-slate-200 font-mono text-xs truncate max-w-[260px]">{p.path}</td>
                      <td className="py-2 text-right text-slate-300">{p.views}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
